import { Button } from '@mantine/core'
import { useState } from 'react'

import { useApi } from '../../api/ApiContext'
import { suspensionOf, type Suspension } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { ConfirmModal } from '../../components/ConfirmModal'
import { IconDelete } from '../../components/icons'
import { LOCALE } from '../../lib/days'
import { clearLocalStorage } from '../../lib/clearLocalStorage'
import { notifyError, notifySaved } from '../../lib/notify'
import { clearPushSubscription } from '../settings/push'

export function formatDeleteDate(iso: string): string {
  return new Date(iso).toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Double-confirmed "Delete account" button: starts the 30-day grace period, clears local storage and signs out.
 * Shared by Settings (Data & integrations) and the public `/delete-account` page so the two never drift apart.
 *
 * [onSuspended]: the account is already scheduled for deletion by moderation (a 403 `{suspended}`), passed instead
 * of the usual toast so the caller can show its own message; without it, that error just shows like any other.
 */
export function DeleteAccountAction(props: { onSuspended?: (info: Suspension) => void }) {
  const auth = useAuth()
  const api = useApi()
  const [step, setStep] = useState<'idle' | 'confirm' | 'final'>('idle')
  const [deleting, setDeleting] = useState(false)

  const deleteAccount = async () => {
    setDeleting(true)
    try {
      const { deleteAt } = await api.deleteAccount()
      notifySaved(`Your account will be deleted on ${formatDeleteDate(deleteAt)}. Sign in again before then to cancel.`)
      await clearPushSubscription(api)
      clearLocalStorage()
      await auth.signOut()
    } catch (error) {
      const suspended = suspensionOf(error)
      if (suspended && props.onSuspended) {
        props.onSuspended(suspended)
      } else {
        notifyError(error)
      }
      setDeleting(false)
      setStep('idle')
    }
  }

  return (
    <>
      <Button variant="danger" leftSection={<IconDelete size={18} />} onClick={() => setStep('confirm')}>
        Delete account
      </Button>
      <ConfirmModal
        opened={step === 'confirm'}
        title="Delete your account?"
        body="This starts a 30-day grace period on your diary, recipes, weight, photos and everything else on our servers. Signing in again before it ends cancels it."
        confirmLabel="Delete"
        onCancel={() => setStep('idle')}
        onConfirm={() => setStep('final')}
      />
      <ConfirmModal
        opened={step === 'final'}
        title="Are you sure?"
        body="After the 30-day grace period ends, your account and its data are gone for good (backups roll off a further 30 days after that)."
        confirmLabel="Delete my account"
        loading={deleting}
        onCancel={() => setStep('idle')}
        onConfirm={() => void deleteAccount()}
      />
    </>
  )
}
